import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { FindOrdersRequestSort } from '~entities/order';

import type { DataTableSorting } from '~shared/ui/data-table';
import { useColumnVisibility } from '~shared/ui/data-table';

import type { OrdersFiltersValue } from './orders-filters';
import { getDefaultOrdersFilter } from './orders-filters';
import { useOrdersTableDataQuery } from './orders-table-queries';

import { ALL_COLUMN_IDS, ColumnId } from '../lib/columns';
import { defaultPagination } from '../lib/table-options';

const COLUMN_TO_SORT_KEY: Partial<
  Record<ColumnId, FindOrdersRequestSort['key']>
> = {
  [ColumnId.ORDER_DATE]: 'orderTimestampUTC',
};

const DEFAULT_SORTING: DataTableSorting = {
  id: ColumnId.ORDER_DATE,
  direction: 'desc',
};

function toApiSort(
  sorting: DataTableSorting
): FindOrdersRequestSort | undefined {
  const key = COLUMN_TO_SORT_KEY[sorting.id as ColumnId];
  if (!key) return undefined;
  return { key, order: sorting.direction === 'asc' ? 'ASC' : 'DESC' };
}

export function useOrdersTable() {
  const defaultFilters = useMemo(() => getDefaultOrdersFilter(), []);
  const methods = useForm<OrdersFiltersValue>({
    defaultValues: defaultFilters,
  });

  const [appliedFilters, setAppliedFilters] =
    useState<OrdersFiltersValue>(defaultFilters);
  const [filtersTouched, setFiltersTouched] = useState(false);
  const [pagination, setPagination] = useState(defaultPagination);
  const [sorting, setSorting] = useState<DataTableSorting>(DEFAULT_SORTING);
  const { visibleColumns, setVisibleColumns, columnVisibility } =
    useColumnVisibility(ALL_COLUMN_IDS, {
      storageKey: 'orders-table:column-visibility',
    });

  const applyFilters = () => {
    setFiltersTouched(true);
    setAppliedFilters(methods.getValues());
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleSortingChange = (newSorting: DataTableSorting) => {
    setSorting(newSorting);
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const { data, isLoading } = useOrdersTableDataQuery({
    ...pagination,
    filters: appliedFilters,
    sort: toApiSort(sorting),
  });

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const hasRows = orders.length > 0;

  const resetFilters = () => {
    const defaults = getDefaultOrdersFilter();
    methods.reset(defaults);
    setFiltersTouched(false);
    setAppliedFilters(defaults);
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const clearSelectFilters = () => {
    methods.setValue('merchantIds', []);
    methods.setValue('posIds', []);
    methods.setValue('statuses', []);
  };

  const activeFiltersCount =
    (appliedFilters.merchantIds.length > 0 ? 1 : 0) +
    (appliedFilters.posIds.length > 0 ? 1 : 0) +
    (appliedFilters.statuses.length > 0 ? 1 : 0);

  return {
    methods,
    orders,
    total,
    isLoading,
    pagination,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    sorting,
    onSortingChange: handleSortingChange,
    visibleColumns,
    onVisibleColumnsChange: setVisibleColumns,
    columnVisibility,
    onPageChange: (page: number) =>
      setPagination((prev) => ({ ...prev, page })),
    onPageSizeChange: (perPage: number) => setPagination({ page: 0, perPage }),
    showOnboarding: !filtersTouched && !hasRows && !isLoading,
    showAddButton: hasRows || filtersTouched,
  };
}
